/**
 * Canvas Layout Hook
 *
 * Calculates positions for cards based on L-ID coordinate system:
 * - X axis = depth (getLidDepth)
 * - Y axis = sequence order within siblings
 */

import { useMemo } from 'react'
import { MarkerType } from '@xyflow/react'
import type { Node, Edge } from '@xyflow/react'
import type { Card, Topic } from '@/types'
import {
  getLidDepth,
  getParentLid,
  findDirectChildren,
  sortLids,
  formatLidForDisplay,
} from '@/utils/lid'
import type { TopicHeaderNodeData } from './TopicHeaderNode'

// Layout constants - more spacious for better readability
const NODE_WIDTH = 240
const NODE_HEIGHT = 120 // Larger to show content preview
const HORIZONTAL_GAP = 80 // Gap between depth levels
const VERTICAL_GAP = 30 // Gap between siblings
const FOCUSED_NODE_WIDTH = 360
const FOCUSED_NODE_HEIGHT = 240
const TOPIC_HEADER_HEIGHT = 100 // Height for topic header node (name + bubble)
const HEADER_LINE_LENGTH = 24 // Line from header to first card
const ARROW_GAP = 8 // Gap between last card and arrow

export interface CardNodeData extends Record<string, unknown> {
  card: Card
  isExpanded: boolean
  isFocused: boolean
  displayLid: string
  depth: number
}

export interface LayoutResult {
  nodes: Node[]
  edges: Edge[]
}

/**
 * Calculate Y positions for a subtree
 * Returns the total height used
 */
function calculateSubtreeLayout(
  lid: string,
  allLids: string[],
  cardsMap: Map<string, Card>,
  positions: Map<string, { x: number; y: number }>,
  startY: number,
  expandedLids: Set<string>
): number {
  const depth = getLidDepth(lid)
  const isExpanded = expandedLids.has(lid)
  const nodeHeight = isExpanded ? FOCUSED_NODE_HEIGHT : NODE_HEIGHT

  // X position based on depth
  const x = (depth - 1) * (NODE_WIDTH + HORIZONTAL_GAP)

  // Get children
  const childLids = sortLids(findDirectChildren(lid, allLids))

  if (childLids.length === 0) {
    // Leaf node - just place at startY
    positions.set(lid, { x, y: startY })
    return nodeHeight
  }

  // If this node is expanded, we need extra space for children
  // to avoid overlap with the expanded parent
  const extraGap = isExpanded ? (FOCUSED_NODE_HEIGHT - NODE_HEIGHT) / 2 : 0

  // Calculate children positions first
  let currentY = startY + extraGap
  let totalChildrenHeight = 0

  for (const childLid of childLids) {
    const childHeight = calculateSubtreeLayout(
      childLid,
      allLids,
      cardsMap,
      positions,
      currentY,
      expandedLids
    )
    currentY += childHeight + VERTICAL_GAP
    totalChildrenHeight += childHeight
  }

  // Remove last gap, add extra gap for expanded parent
  if (childLids.length > 0) {
    totalChildrenHeight += (childLids.length - 1) * VERTICAL_GAP
    totalChildrenHeight += extraGap * 2 // Top and bottom extra gap
  }

  // Position this node in the middle of its children
  const firstChildPos = positions.get(childLids[0])!
  const lastChildPos = positions.get(childLids[childLids.length - 1])!
  const lastChildHeight = expandedLids.has(childLids[childLids.length - 1])
    ? FOCUSED_NODE_HEIGHT
    : NODE_HEIGHT
  const childrenMidY = (firstChildPos.y + lastChildPos.y + lastChildHeight) / 2 - nodeHeight / 2

  positions.set(lid, { x, y: childrenMidY })

  // Return the total height used by this subtree
  return Math.max(nodeHeight, totalChildrenHeight)
}

/**
 * Main layout hook
 */
export function useCanvasLayout(
  cards: Card[],
  topic: Topic,
  expandedLids: Set<string>,
  focusLid?: string | null
): LayoutResult {
  return useMemo(() => {
    const nodes: Node[] = []
    const edges: Edge[] = []

    if (cards.length === 0) {
      // Show header even with no cards
      nodes.push({
        id: 'topic-header',
        type: 'topicHeader',
        position: { x: NODE_WIDTH / 2 - 60, y: 0 },
        data: {
          topicId: topic.id,
          topicName: topic.name,
        } as TopicHeaderNodeData,
        draggable: false,
        selectable: false,
      })
      return { nodes, edges }
    }

    // Build maps for quick lookup
    const cardsMap = new Map<string, Card>()
    const allLids: string[] = []

    for (const card of cards) {
      cardsMap.set(card.lid, card)
      allLids.push(card.lid)
    }

    // Find root cards (no parent in our set)
    const rootLids = sortLids(
      allLids.filter((lid) => {
        const parentLid = getParentLid(lid)
        return !parentLid || !cardsMap.has(parentLid)
      })
    )

    // Calculate positions - start below the header (header height + line length)
    const positions = new Map<string, { x: number; y: number }>()
    let currentY = TOPIC_HEADER_HEIGHT + HEADER_LINE_LENGTH

    for (const rootLid of rootLids) {
      const subtreeHeight = calculateSubtreeLayout(
        rootLid,
        allLids,
        cardsMap,
        positions,
        currentY,
        expandedLids
      )
      currentY += subtreeHeight + VERTICAL_GAP * 2 // Extra gap between root trees
    }

    // Add topic header node - positioned directly above the first card
    const firstRootPos = positions.get(rootLids[0])
    if (firstRootPos) {
      nodes.push({
        id: 'topic-header',
        type: 'topicHeader',
        position: {
          x: firstRootPos.x + NODE_WIDTH / 2 - 60,
          y: firstRootPos.y - TOPIC_HEADER_HEIGHT - HEADER_LINE_LENGTH,
        },
        data: {
          topicId: topic.id,
          topicName: topic.name,
        } as TopicHeaderNodeData,
        draggable: false,
        selectable: false,
      })
    }

    // Create card nodes
    for (const [lid, pos] of positions) {
      const card = cardsMap.get(lid)
      if (!card) continue

      const isExpanded = expandedLids.has(lid)
      const isFocused = focusLid === lid
      const depth = getLidDepth(lid)

      nodes.push({
        id: lid,
        type: 'cardNode',
        position: pos,
        data: {
          card,
          isExpanded,
          isFocused,
          displayLid: formatLidForDisplay(lid),
          depth,
        } as CardNodeData,
        style: {
          width: isExpanded ? FOCUSED_NODE_WIDTH : NODE_WIDTH,
          height: isExpanded ? FOCUSED_NODE_HEIGHT : NODE_HEIGHT,
        },
        // Bring focused card to front
        zIndex: isFocused ? 1000 : undefined,
      })
    }

    // Timeline end node (arrow at the bottom)
    const lastRootLid = rootLids[rootLids.length - 1]
    const lastRootPos = positions.get(lastRootLid)
    if (lastRootPos) {
      const lastNodeHeight = expandedLids.has(lastRootLid) ? FOCUSED_NODE_HEIGHT : NODE_HEIGHT
      nodes.push({
        id: 'timeline-end',
        type: 'timelineEnd',
        position: {
          x: lastRootPos.x + NODE_WIDTH / 2 - 24,
          y: lastRootPos.y + lastNodeHeight + ARROW_GAP,
        },
        data: {},
        draggable: false,
        selectable: false,
      })
    }

    // Create edges

    // Header to first root card edge
    if (rootLids.length > 0) {
      edges.push({
        id: 'header-to-first',
        source: 'topic-header',
        target: rootLids[0],
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'default', // bezier curve
        style: {
          stroke: '#334155',
          strokeWidth: 2,
        },
        animated: false,
      })
    }

    // Parent-child edges (horizontal connections with bezier curves)
    for (const lid of allLids) {
      const parentLid = getParentLid(lid)
      if (parentLid && positions.has(parentLid)) {
        edges.push({
          id: `${parentLid}-${lid}`,
          source: parentLid,
          target: lid,
          type: 'default', // bezier curve
          style: {
            stroke: '#94a3b8',
            strokeWidth: 2,
          },
          animated: false,
        })
      }
    }

    // Timeline edges (vertical connections for root cards)
    // Root card -> next root card (bezier curve connections)
    for (let i = 0; i < rootLids.length - 1; i++) {
      const sourceLid = rootLids[i]
      const targetLid = rootLids[i + 1]
      edges.push({
        id: `timeline-${sourceLid}-${targetLid}`,
        source: sourceLid,
        target: targetLid,
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'default', // bezier curve
        style: {
          stroke: '#334155',
          strokeWidth: 2,
        },
        animated: false,
      })
    }

    // Last root card to timeline-end edge
    if (rootLids.length > 0) {
      edges.push({
        id: 'last-to-end',
        source: rootLids[rootLids.length - 1],
        target: 'timeline-end',
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'default', // bezier curve
        style: {
          stroke: '#334155',
          strokeWidth: 2,
        },
        animated: false,
      })
    }

    // Child sibling edges (for branches)
    const siblingGroups = new Map<string, string[]>()
    for (const lid of allLids) {
      const parentLid = getParentLid(lid)
      if (parentLid && positions.has(parentLid)) {
        if (!siblingGroups.has(parentLid)) {
          siblingGroups.set(parentLid, [])
        }
        siblingGroups.get(parentLid)!.push(lid)
      }
    }

    // Create sibling edges for branch children
    for (const [, siblings] of siblingGroups) {
      if (siblings.length > 1) {
        const sortedSiblings = sortLids(siblings)
        for (let i = 0; i < sortedSiblings.length - 1; i++) {
          const sourceLid = sortedSiblings[i]
          const targetLid = sortedSiblings[i + 1]
          edges.push({
            id: `sibling-${sourceLid}-${targetLid}`,
            source: sourceLid,
            target: targetLid,
            sourceHandle: 'bottom',
            targetHandle: 'top',
            type: 'default', // bezier curve
            style: {
              stroke: '#cbd5e1',
              strokeWidth: 2,
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 10,
              height: 10,
              color: '#cbd5e1',
            },
            animated: false,
          })
        }
      }
    }

    return { nodes, edges }
  }, [cards, topic, expandedLids, focusLid])
}

/**
 * Get navigation targets for keyboard navigation
 */
export function getNavigationTargets(
  currentLid: string,
  allLids: string[],
  cardsMap: Map<string, Card>
): {
  parent: string | null
  firstChild: string | null
  prevSibling: string | null
  nextSibling: string | null
} {
  const parentLid = getParentLid(currentLid)
  const parent = parentLid && cardsMap.has(parentLid) ? parentLid : null

  // Get children
  const children = sortLids(findDirectChildren(currentLid, allLids))
  const firstChild = children.length > 0 ? children[0] : null

  // Get siblings
  let siblings: string[]
  if (parentLid) {
    siblings = sortLids(findDirectChildren(parentLid, allLids))
  } else {
    // Root level - find other roots
    siblings = sortLids(
      allLids.filter((lid) => {
        const p = getParentLid(lid)
        return !p || !cardsMap.has(p)
      })
    )
  }

  const currentIndex = siblings.indexOf(currentLid)
  const prevSibling = currentIndex > 0 ? siblings[currentIndex - 1] : null
  const nextSibling = currentIndex < siblings.length - 1 ? siblings[currentIndex + 1] : null

  return { parent, firstChild, prevSibling, nextSibling }
}
